import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    BookOpen,
    BookOpenCheck,
    FilePlus,
    ListChecks,
    Loader2,
    Pencil,
    Plus,
    Search,
    Trash2,
    X,
} from 'lucide-react';
import VectorChunkAuditModal from './VectorChunkAuditModal';
import UploadToKnowledgebaseModal from './UploadToKnowledgebaseModal';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import AddDocumentsToKbModal from './AddDocumentsModal';
import { useTheme } from '../../../../components/common/Theme';
import EmptyState from '../../governed-mcp-gateway/components/EmptyState';
import { primaryBtn } from '../../governed-mcp-gateway/components/GatewayUi';

const TENANT_ID = '3fa85f64-5717-4562-b3fc-2c963f66afa6';
const USER_ID = 'f092eac4-3bf7-4133-8156-4f4d08436b77';

function asText(value: unknown, fallback = '—'): string {
    if (value == null || value === '') return fallback;
    if (Array.isArray(value)) {
        const joined = value.map((item) => String(item ?? '').trim()).filter(Boolean).join(', ');
        return joined || fallback;
    }
    const text = String(value).trim();
    return text || fallback;
}

function RowIconButton({
    label,
    icon: Icon,
    onClick,
    danger = false,
    isLight,
}: {
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    onClick: () => void;
    danger?: boolean;
    isLight: boolean;
}) {
    const tone = danger
        ? isLight
            ? 'text-neutral-400 hover:text-red-600 hover:bg-red-50'
            : 'text-neutral-500 hover:text-red-300 hover:bg-red-500/10'
        : isLight
            ? 'text-neutral-400 hover:text-indigo-600 hover:bg-indigo-50'
            : 'text-neutral-500 hover:text-white hover:bg-white/10';

    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            onClick={onClick}
            className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${tone}`}
        >
            <Icon size={14} />
        </button>
    );
}

const KnowledgebaseManagementModal = ({
    onClose,
    workspaceid,
    kbId,
}: {
    onClose: () => void;
    workspaceid: string;
    kbId?: string | null;
}) => {
    const { theme } = useTheme();
    const isLight = theme === 'white';
    const [kbs, setKbs] = useState<any[]>([]);
    const [search, setSearch] = useState(() => kbId?.trim() ?? '');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showChunks, setShowChunks] = useState(false);
    const [selectedKb, setSelectedKb] = useState<any | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
    const [editKbId, setEditKbId] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
    const [kbToDelete, setKbToDelete] = useState<any | null>(null);
    const [showAddDocsModal, setShowAddDocsModal] = useState(false);
    const [targetKb, setTargetKb] = useState<any | null>(null);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !showUploadModal && !showChunks && !kbToDelete && !showAddDocsModal) {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [kbToDelete, onClose, showAddDocsModal, showChunks, showUploadModal]);

    const fetchKbs = useCallback(async () => {
        if (!workspaceid) return;
        setLoading(true);
        setError(null);
        try {
            const response = await knowledgebaseService.getKnowledgeBasesBySourceId(workspaceid);
            setKbs(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('Failed to load knowledge bases', err);
            setError('Failed to load knowledge bases.');
            setKbs([]);
        } finally {
            setLoading(false);
        }
    }, [workspaceid]);

    useEffect(() => {
        fetchKbs();
    }, [fetchKbs]);

    const filteredKbs = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return kbs;

        return kbs.filter((kb) => {
            const title = asText(kb.title, '').toLowerCase();
            const description = asText(kb.description, '').toLowerCase();
            const id = asText(kb.id, '').toLowerCase();
            return title.includes(query) || description.includes(query) || id.includes(query);
        });
    }, [kbs, search]);

    const handleDeleteKnowledgeBase = async () => {
        if (!kbToDelete) return;

        try {
            await knowledgebaseService.deleteKnowledgeBase({
                knowledgeBaseId: kbToDelete.id,
                tenantId: TENANT_ID,
            });

            setKbs((prev) => prev.filter((kb) => kb.id !== kbToDelete.id));
            setKbToDelete(null);
        } catch (err) {
            console.error('Failed to delete KB:', err);
            alert('Failed to delete the knowledge base.');
            setKbToDelete(null);
        }
    };

    const handleViewDocuments = async (kb: any, dataSourceName: string) => {
        try {
            const responseFromApi = await knowledgebaseService.getUploadedDocuments(kb.id);
            setSelectedKb({ ...kb, sourceName: dataSourceName });
            setUploadedFiles(responseFromApi);
            setShowChunks(true);
        } catch (err) {
            console.error('Failed to fetch documents', err);
        }
    };

    const openCreate = () => {
        setIsEditMode(false);
        setEditKbId(null);
        setShowUploadModal(true);
    };

    const panel = isLight
        ? 'bg-white border-neutral-200/80 shadow-[0_24px_80px_rgba(16,24,40,0.18)]'
        : 'bg-neutral-950 border-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.55)]';
    const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
    const divider = isLight ? 'border-neutral-200/80' : 'border-white/10';

    const scrollClass = `min-h-0 flex-1 overflow-auto overscroll-contain [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full ${
        isLight
            ? '[scrollbar-color:rgba(99,102,241,0.45)_transparent] [&::-webkit-scrollbar-thumb]:bg-indigo-400/50'
            : '[scrollbar-color:rgba(165,180,252,0.45)_transparent] [&::-webkit-scrollbar-thumb]:bg-indigo-300/40'
    }`;

    return createPortal(
        <div className="fixed inset-0 z-[80] overflow-hidden">
            <button
                type="button"
                aria-label="Close knowledge base management"
                className={`absolute inset-0 ${isLight ? 'bg-neutral-900/40' : 'bg-black/70'} backdrop-blur-md`}
                onClick={onClose}
            />

            <div className="relative z-10 flex h-full w-full items-center justify-center p-3 sm:p-6 pointer-events-none">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="kb-management-title"
                className={`pointer-events-auto flex w-full max-w-6xl flex-col overflow-hidden rounded-3xl border ${panel}`}
                style={{ height: 'min(880px, calc(100dvh - 1.5rem))', maxHeight: 'calc(100dvh - 1.5rem)' }}
            >
                <div className={`shrink-0 flex items-start justify-between gap-4 px-6 py-5 border-b ${divider}`}>
                    <div className="flex items-start gap-3 min-w-0">
                        <span className={`mt-0.5 h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'}`}>
                            <BookOpenCheck size={20} />
                        </span>
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 id="kb-management-title" className={`text-lg font-semibold tracking-tight ${isLight ? 'text-neutral-950' : 'text-white'}`}>
                                    Knowledge bases
                                </h2>
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-300'}`}>
                                    {loading ? '…' : kbs.length}
                                </span>
                            </div>
                            <p className={`mt-0.5 text-sm ${muted}`}>
                                Every knowledge base in this workspace, in one listing.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openCreate}>
                            <Plus size={15} /> Add New
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className={`h-9 w-9 inline-flex items-center justify-center rounded-full transition-colors ${isLight ? 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900' : 'text-neutral-400 hover:bg-white/10 hover:text-white'}`}
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                <div className={`shrink-0 px-6 py-4 border-b ${divider} flex flex-col sm:flex-row sm:items-center gap-3`}>
                    <div className="relative flex-1">
                        <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
                        <input
                            type="text"
                            placeholder="Search by title, description, or ID"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={`w-full pl-10 pr-10 py-2.5 rounded-xl text-sm outline-none transition-shadow ${
                                isLight
                                    ? 'bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400'
                                    : 'bg-white/[0.04] border border-white/10 text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400'
                            }`}
                        />
                        {search ? (
                            <button
                                type="button"
                                aria-label="Clear search"
                                onClick={() => setSearch('')}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-lg ${isLight ? 'text-neutral-400 hover:bg-neutral-200/70' : 'text-neutral-500 hover:bg-white/10'}`}
                            >
                                <X size={14} />
                            </button>
                        ) : null}
                    </div>
                    <p className={`text-xs font-medium tabular-nums shrink-0 ${muted}`}>
                        {loading ? 'Loading…' : `${filteredKbs.length} of ${kbs.length}`}
                        {!loading && search.trim() ? ' matching' : ''}
                    </p>
                </div>

                <div className={`${scrollClass} ${isLight ? 'bg-neutral-50/70' : 'bg-black/20'}`}>
                    {error ? <p className="px-6 pt-4 text-sm text-red-400">{error}</p> : null}
                    {loading ? (
                        <div className={`flex flex-col items-center justify-center py-20 ${muted}`} aria-busy="true">
                            <Loader2 className="w-7 h-7 animate-spin text-indigo-400 mb-3" />
                            <span className="text-sm">Loading knowledge bases…</span>
                        </div>
                    ) : filteredKbs.length === 0 ? (
                        <EmptyState
                            isLight={isLight}
                            title={kbs.length === 0 ? 'No knowledge bases yet' : 'No matching knowledge bases'}
                            description={
                                kbs.length === 0
                                    ? 'Create a knowledge base to start adding documents and access rules.'
                                    : 'Try another title, description, or ID.'
                            }
                            action={
                                kbs.length === 0 ? (
                                    <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openCreate}>
                                        <Plus size={14} /> Add New
                                    </button>
                                ) : undefined
                            }
                        />
                    ) : (
                        <ul className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                            {filteredKbs.map((kb) => {
                                const title = asText(kb.title, 'Untitled knowledge base');
                                const highlighted = Boolean(kbId && kb.id === kbId);
                                const facts = [
                                    ['Roles', asText(kb.roles)],
                                    ['Users', asText(kb.users)],
                                    ['Departments', asText(kb.departments)],
                                    ['Chunking', asText(kb.chunking)],
                                    ['Denied users', asText(kb.deniedUsers, 'None')],
                                    ['Audit', asText(kb.auditInfo)],
                                ];
                                return (
                                    <li
                                        key={kb.id}
                                        className={`flex flex-col rounded-2xl p-4 ${
                                            highlighted
                                                ? isLight ? 'bg-white ring-2 ring-indigo-400 shadow-[0_8px_24px_rgba(79,70,229,0.08)]' : 'bg-neutral-900 ring-2 ring-indigo-400/70'
                                                : isLight
                                                    ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.05]'
                                                    : 'bg-neutral-900 ring-1 ring-white/10'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'}`}>
                                                <BookOpen size={18} />
                                            </span>
                                            <h3 className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`} title={title}>
                                                {title}
                                            </h3>
                                        </div>
                                        <p className={`mt-3 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem] ${muted}`} title={asText(kb.description, '')}>
                                            {asText(kb.description, 'No description')}
                                        </p>
                                        <dl className={`mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs ${isLight ? 'border-t border-neutral-100 pt-3' : 'border-t border-white/10 pt-3'}`}>
                                            {facts.map(([label, value]) => (
                                                <div key={label} className="min-w-0">
                                                    <dt className={`text-[10px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{label}</dt>
                                                    <dd className={`mt-0.5 truncate ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`} title={value}>{value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                        <div className={`mt-3 flex items-center justify-between gap-2 pt-3 ${isLight ? 'border-t border-neutral-100' : 'border-t border-white/10'}`}>
                                            <div className="flex items-center">
                                                <RowIconButton isLight={isLight} label="Add data" icon={FilePlus} onClick={() => { setTargetKb(kb); setShowAddDocsModal(true); }} />
                                                <RowIconButton isLight={isLight} label="Logs" icon={ListChecks} onClick={() => handleViewDocuments(kb, kb.title)} />
                                                <RowIconButton isLight={isLight} label="Edit" icon={Pencil} onClick={() => { setIsEditMode(true); setEditKbId(kb.id); setShowUploadModal(true); }} />
                                                <RowIconButton isLight={isLight} label="Delete" icon={Trash2} danger onClick={() => setKbToDelete(kb)} />
                                            </div>
                                            <p className={`min-w-0 truncate font-mono text-[10px] ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} title={asText(kb.id, '')}>
                                                {asText(kb.id)}
                                            </p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
            </div>

            {showUploadModal && workspaceid && (
                <UploadToKnowledgebaseModal
                    tenantId={TENANT_ID}
                    userId={USER_ID}
                    isEditMode={isEditMode}
                    existingKbId={editKbId || undefined}
                    workspaceid={workspaceid}
                    onClose={() => {
                        setShowUploadModal(false);
                        setEditKbId(null);
                        setIsEditMode(false);
                    }}
                    onSubmit={() => {
                        setShowUploadModal(false);
                        setEditKbId(null);
                        setIsEditMode(false);
                        fetchKbs();
                    }}
                />
            )}

            {showChunks && selectedKb && (
                <VectorChunkAuditModal
                    kbTitle={selectedKb.title}
                    sourceName={selectedKb.sourceName || 'Unknown Source'}
                    files={uploadedFiles}
                    onClose={() => {
                        setShowChunks(false);
                        setSelectedKb(null);
                    }}
                />
            )}

            {kbToDelete && (
                <ConfirmDialog
                    message={`Are you sure you want to delete the knowledge base "${kbToDelete.title}" and all its documents?`}
                    onYes={handleDeleteKnowledgeBase}
                    onNo={() => setKbToDelete(null)}
                    onCancel={() => setKbToDelete(null)}
                />
            )}

            {showAddDocsModal && targetKb && (
                <AddDocumentsToKbModal
                    tenantId={TENANT_ID}
                    knowledgeBaseId={targetKb.id}
                    kbTitle={targetKb.title}
                    onClose={() => {
                        setShowAddDocsModal(false);
                        setTargetKb(null);
                    }}
                />
            )}
        </div>,
        document.body,
    );
};

export default KnowledgebaseManagementModal;
